import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { NgClass } from '@angular/common';
import { UserProfileService } from '../../../core/api/services/user-profile/user-profile.service';
import { UrlConstants } from '../../../core/constants/url.constants';
import { ForgotPasswordRequest } from '../../../core/models/user-profile/forgot-password-request';
import { ForgotCredentialsResponse } from '../../../core/models/user-profile/forgot-credentials-response';
import { SessionStorageConstants } from '../../../core/constants/session-storage.constant';
import { emailValidator } from '../../../core/util/custom-form-validators-util';
import { StandardFormFieldComponent } from '../../common/standard-form-field/standard-form-field.component';
import { Message, ValidationMessagesPanelComponent } from '../../common/validation-messages-panel/validation-messages-panel.component';

@Component({
  selector: 'app-forgot-password',
  imports: [
    TranslatePipe,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    RouterLink,
    StandardFormFieldComponent,
    ValidationMessagesPanelComponent
  ],
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.css']
})
export class ForgotPasswordComponent implements OnInit {

  readonly userProfileService = inject(UserProfileService);
  readonly translateService = inject(TranslateService);
  readonly router = inject(Router);
  readonly activatedRoute = inject(ActivatedRoute);
  readonly UrlConstants = UrlConstants;

  validationMessages: Message[] = [];
  errors: string[] = [];
  submitted = false;
  emailSent = false;
  loading: boolean = false;
  emailAddress: string = '';
  username: string = '';

  forgotPasswordForm: FormGroup = new FormGroup({
    username: new FormControl('', [Validators.required]),
    emailAddress: new FormControl('', [Validators.required, emailValidator()])
  });

  ngOnInit() {
    // Check if coming from confirmation (query param)
    this.activatedRoute.queryParams.subscribe(params => {
      if (params['email']) {
        this.emailAddress = params['email'];
        this.forgotPasswordForm.patchValue({ emailAddress: this.emailAddress });
      }
    });
  }

  submit() {
    this.errors = [];
    this.submitted = true;
    this.forgotPasswordForm.get('username')?.markAsDirty();
    this.forgotPasswordForm.get('emailAddress')?.markAsDirty();

    if (this.forgotPasswordForm.valid) {
      this.loading = true;
      const username = this.forgotPasswordForm.value.username;
      const emailAddress = this.forgotPasswordForm.value.emailAddress;

      // Create typed request object
      const request: ForgotPasswordRequest = {
        username: username,
        emailAddress: emailAddress
      };

      this.userProfileService.forgotPassword(request).subscribe({
        next: (response: ForgotCredentialsResponse) => {

          this.loading = false;

          const outcome = response?.outcome || '';
          if (outcome && outcome.includes('forgotPasswordConfirmation')) {
            // Success - show confirmation
            this.emailSent = true;
            this.emailAddress = request.emailAddress;
            this.username = request.username;
            this.errors = [];
            const successMsg: Message = {
              message: this.translateService.instant('userprofile.text.forgotPassword.successMessage'),
              type: 'info'
            };
            this.validationMessages.push(successMsg);
          } else {
            // Error - use errMsg translation key from backend
            const errMsgKey = response?.errMsg || 'validation.forgotPasswordInfoNotFound';
            this.errors.push(this.translateService.instant('userprofile.' + errMsgKey, {
              0: SessionStorageConstants.CRC_DEV_SUPPORT_MAIL,
              1: SessionStorageConstants.CRC_DEV_SUPPORT_MAIL
            }));
          }
        },
        error: (error) => {
          this.loading = false;
          // Handle HTTP errors - use errMsg from error response if available
          const errorResponse: ForgotCredentialsResponse = error.error || {};
          const errMsgKey = errorResponse.errMsg || 'validation.forgotPasswordInfoNotFound';
          this.errors.push(this.translateService.instant('userprofile.' + errMsgKey));
        }
      });
    } else {
      if (!this.forgotPasswordForm.value.username) {
        const userName = this.translateService.instant('userprofile.label.username');
        this.errors.push(this.translateService.instant('userprofile.validation.confirmPassword.isRequired', { 0: userName }));
      }
      if (!this.forgotPasswordForm.value.emailAddress) {
        const email = this.translateService.instant('userprofile.label.email');
        this.errors.push(this.translateService.instant('userprofile.validation.confirmPassword.isRequired', { 0: email }));
      } else if (this.forgotPasswordForm.get('emailAddress')?.hasError('email')) {
        this.errors.push(this.translateService.instant('userprofile.validation.invalidEmail'));
      }

    }
  }
}
